import React, { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { theme } from '../../../../theme/theme'
import { Divider } from '../../../common/Divider'
import { UserAvatar } from '../../../common/Avatar'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useIsMobile } from '../../../../hooks/useIsMobile'
import { Button, ButtonVariant } from '../../../common/Button'
import { FacilityBookingListItem } from './FacilityBookingListItem'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { useUserUnitsString } from '../../../../hooks/useUserUnitsString'
import { userQueries } from '../../../../api/users'
import { getUserFullName } from '../../../../helpers/user'
import { societyQueries } from '../../../../api/societies'
import { getTimeSpanString } from '../../../../state/models/booking'
import { Badge } from '../../../common/Badge'

interface FacilityBookingListItemInner extends FacilityBookingListItem {
  openDeleteBookingModal: () => void
  deleteBookingAllowed: boolean
}

export const FacilityBookingListItemInner = ({
  openDeleteBookingModal,
  deleteBookingAllowed,
  booking,
  facility,
}: FacilityBookingListItemInner): ReactNode => {
  const { translate } = useAppTranslation()
  const isMobile = useIsMobile()
  const navigate = useNavigate()

  const { data: user } = useQuery(userQueries.getUser(booking.userId))
  const { data: society } = useQuery(
    societyQueries.getSociety(booking.societyId)
  )

  const userUnitsString = useUserUnitsString(user?._id, booking.societyId)

  const getUserRoute = (): string => {
    return reverseUrl('user:society-detail', {
      id: user?._id,
      societyId: society?._id,
    })
  }

  const userOnClick = (): void => {
    return navigate(getUserRoute())
  }

  const timeSpanString =
    facility?.bookingGranularity === 'full-day'
      ? translate('facilityView.calendar.fullDay')
      : getTimeSpanString(booking)

  const fullName = getUserFullName(user)
  return (
    <>
      <div className="flex items-center justify-between py-5">
        <div className="flex items-center">
          <UserAvatar user={user} onClick={userOnClick} />
          <div className="ml-2 pr-2">
            {booking.isAdmin && (
              <Badge
                padding="px-1.5"
                colorClass="bg-brandGreen/10"
                borderColorClass="border-brandGreen"
                textColorClass="text-black"
                text="Admin"
                className="justify-self-start mb-1 w-fit"
              />
            )}
            <Link
              className="hover:underline justify-self-start"
              to={getUserRoute()}
              style={theme.textVariants.baseBold}
              translate="no"
            >
              {fullName}
            </Link>
            {userUnitsString && (
              <div className="mt-[2px] flex items-center">
                <p
                  className="text-gray-600 line-clamp-1"
                  style={
                    isMobile
                      ? theme.textVariants.caption
                      : theme.textVariants.base
                  }
                >
                  {userUnitsString}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-end">
          {!isMobile && deleteBookingAllowed && (
            <Button
              label={translate('facilityView.bookings.buttons.deleteBooking')}
              variant={ButtonVariant.DANGER}
              className="mr-6"
              onClick={openDeleteBookingModal}
            />
          )}
          <div className="flex flex-col text-right">
            <p
              style={
                isMobile ? theme.textVariants.caption : theme.textVariants.base
              }
            >
              {dayjs(booking.startDate).format(
                isMobile ? 'DD MMM YYYY' : 'DD MMMM YYYY'
              )}
            </p>
            <p
              style={
                isMobile ? theme.textVariants.caption : theme.textVariants.base
              }
              className="text-neutral-40"
            >
              {timeSpanString}
            </p>
          </div>
        </div>
      </div>
      <Divider />
    </>
  )
}
